import { useTranslation } from "react-i18next";
import { RecurringDefectsMetrics } from "../../types/Metric";
import { useCallback, useMemo } from "react";
import { RecurringDefectsGraph } from "../RecurringDefectsTab/RecurringDefectsGraph";
import { Button } from "primereact/button";
import { buildRecurringDefectHoverTemplate, mapDefectsMetrics, prepareRecurringDefectMetrics } from "../../utils/DashboardHelper";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { DefectTabs } from "../../types/DefectTabs";
import { setActiveTab } from "../../store/slices/defectSlice";
import { sendGAEvent } from "../../AnalyticsService";
import { DASHBOARD_CATEGORY, DASHBOARD_RECURRING_DEFECTS_DETAILED_VIEW_CLICK, DASHBOARD_RECURRING_DEFECTS_BAR_CLICK } from "../../constants/analytics";
import { PlotMouseEvent } from "plotly.js";
import { defaultDefectsFiltersConfig } from "../DefectsTable/Filters/FilterTemplates";
import { DefectTableFields } from "../DefectsTable/DefectTableFields";
import { FilterMatchMode } from "primereact/api";
import { resetTableFilters } from "../../store/thunks/filtersThunks";
import LOCAL_TABLE_CONFIG_KEY from "../../types/TableLocalConfigKeys";
import { selectDefectCodes } from "../../store/selectors/defectSelectors";

type Props = {
    metrics: RecurringDefectsMetrics | undefined;
    loading: boolean;
    maxItems: number;
};

export const RecurringDefectsTiles = (props: Props) => {
    const { metrics, loading, maxItems } = props;

    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const defectCodes = useAppSelector(selectDefectCodes);

    const data = useMemo(() => {
        if (!metrics?.recurring_defects) {
            return mapDefectsMetrics(undefined, (metric) => buildRecurringDefectHoverTemplate(metric, defectCodes));
        }
        
        const metricsWithTotals = prepareRecurringDefectMetrics(metrics.recurring_defects);
        
        return mapDefectsMetrics(
            metricsWithTotals,
            (metric) => buildRecurringDefectHoverTemplate(metric, defectCodes)
        );
    }, [metrics, defectCodes]);

    const onClick = useCallback(() => {
        sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_RECURRING_DEFECTS_DETAILED_VIEW_CLICK);
        navigate("/defects");
        dispatch(setActiveTab(DefectTabs.recurringDefects));
    }, []);

    const header = useMemo(
        () => (
            <div className="flex align-items-center">
                <h2>{t("dashboard.recurringDefects")}</h2>
                <div className="flex-grow-1" />
                <p>{t("dashboard.fromLatestAndPreviousInspections")}</p>
                <Button
                    label={t("dashboard.viewMore")}
                    text
                    onClick={onClick}
                />
            </div>
        ),
        []
    );

    const onBarClick = useCallback(
        (event: PlotMouseEvent) => {
            sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_RECURRING_DEFECTS_BAR_CLICK);

            if (event.points.length && metrics?.recurring_defects) {
                const { y, data, pointIndex } = event.points[0];
                const typeCode = data.customdata[pointIndex];
                if (y && typeof y === "string" && typeof typeCode === "string") {
                    // Find the metric with this type code to get IMO numbers
                    const metric = metrics.recurring_defects.find(m => m.type_code === typeCode);
                    const imoNumbers = metric?.imo_numbers?.map(String) || [];

                    const tableFilters = {
                        ...defaultDefectsFiltersConfig,
                        [DefectTableFields.STATUS]: {
                            value: null,
                            matchMode: FilterMatchMode.IN
                        },
                        [DefectTableFields.TYPE_CODE]: {
                            value: [typeCode],
                            matchMode: FilterMatchMode.IN
                        },
                        ...(imoNumbers.length > 0 && {
                            [DefectTableFields.IMO]: {
                                value: imoNumbers,
                                matchMode: FilterMatchMode.IN
                            }
                        })
                    };

                    dispatch(
                        resetTableFilters(
                            LOCAL_TABLE_CONFIG_KEY.DEFECTS,
                            tableFilters
                        )
                    );

                    navigate("/defects");
                    dispatch(setActiveTab(DefectTabs.defects));
                }
            }
        },
        [metrics, dispatch, navigate]
    );

    if (!loading && !data?.length) {
        return (
            <div className="col-12">
                <div className="metric-card p-4 pt-3">
                    {header}
                    <div className="flex justify-content-center">
                        <p>{t("dashboard.noDefectsDataAvailable")}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="col-12">
            <div className="metric-card p-4 pt-3">
                {header}
                <RecurringDefectsGraph
                    className="defect-insight-graph-small"
                    data={data}
                    filterValues={[]}
                    loading={loading}
                    onClick={onBarClick}
                    maxItems={maxItems}
                />
            </div>
        </div>
    );
};
